import { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import './CreditPackages.css';

type CreditPackage = {
  id: string;
  name: string;
  description?: string;
  credits: number;
  priceUsd: number;
  active: boolean;
  stripePriceId?: string;
  pixPrice?: number;
  pixDiscountPercent?: number;
  pixShow?: boolean;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
};

const emptyForm: Omit<CreditPackage, 'id'> = {
  name: '',
  description: '',
  credits: 1000,
  priceUsd: 99.0,
  active: true,
  stripePriceId: '',
  pixPrice: 0,
  pixDiscountPercent: 0,
  pixShow: false,
};

export default function CreditPackages() {
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  const loadPackages = async () => {
    setLoading(true);
    setError(null);
    try {
      const ref = collection(db, 'creditPackages');
      const snapshot = await getDocs(query(ref, orderBy('createdAt', 'desc')));
      const list = snapshot.docs.map(
        (d) =>
          ({
            id: d.id,
            ...d.data(),
          } as CreditPackage),
      );
      setPackages(list);
    } catch (err) {
      console.error(err);
      setError('Failed to load credit packages.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPackages();
  }, []);

  const handleEdit = (pkg: CreditPackage) => {
    setEditingId(pkg.id);
    setFormData({
      name: pkg.name,
      description: pkg.description || '',
      credits: pkg.credits,
      priceUsd: pkg.priceUsd,
      active: pkg.active,
      stripePriceId: pkg.stripePriceId || '',
      pixPrice: pkg.pixPrice ?? 0,
      pixDiscountPercent: pkg.pixDiscountPercent ?? 0,
      pixShow: pkg.pixShow ?? false,
    });
    setSuccess(null);
    setError(null);
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData(emptyForm);
  };

  const openCreate = () => {
    resetForm();
    setError(null);
    setSuccess(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (editingId) {
        await updateDoc(doc(db, 'creditPackages', editingId), {
          name: formData.name,
          description: formData.description || '',
          credits: Number(formData.credits),
          priceUsd: Number(formData.priceUsd),
          active: Boolean(formData.active),
          stripePriceId: formData.stripePriceId || null,
          pixPrice: Number(formData.pixPrice) || 0,
          pixDiscountPercent: Number(formData.pixDiscountPercent) || 0,
          pixShow: Boolean(formData.pixShow),
          updatedAt: serverTimestamp(),
        });
        setSuccess('Package updated.');
      } else {
        await addDoc(collection(db, 'creditPackages'), {
          name: formData.name,
          description: formData.description || '',
          credits: Number(formData.credits),
          priceUsd: Number(formData.priceUsd),
          active: Boolean(formData.active),
          stripePriceId: formData.stripePriceId || null,
          pixPrice: Number(formData.pixPrice) || 0,
          pixDiscountPercent: Number(formData.pixDiscountPercent) || 0,
          pixShow: Boolean(formData.pixShow),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        setSuccess('Package created.');
      }
      setShowModal(false);
      resetForm();
      await loadPackages();
    } catch (err) {
      console.error(err);
      setError('Failed to save package.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pkg: CreditPackage) => {
    if (!window.confirm(`Delete package "${pkg.name}"?`)) return;
    try {
      await deleteDoc(doc(db, 'creditPackages', pkg.id));
      setSuccess('Package deleted.');
      if (editingId === pkg.id) {
        resetForm();
      }
      await loadPackages();
    } catch (err) {
      console.error(err);
      setError('Failed to delete package.');
    }
  };

  const sortedPackages = useMemo(
    () =>
      packages.sort((a, b) => {
        if (a.active === b.active) return b.credits - a.credits;
        return a.active ? -1 : 1;
      }),
    [packages],
  );

  return (
    <div className="credit-packages-page">
      <header>
        <div>
          <h1>Client Credit Packages</h1>
          <p>Configure the packages that appear in the client billing page.</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={loadPackages} disabled={loading}>
            Refresh
          </button>
          <button className="btn-primary" onClick={openCreate}>
            New Package
          </button>
        </div>
      </header>

      {(error || success) && (
        <div className="alerts">
          {error && !showModal && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}
        </div>
      )}

      <section className="packages-list">
        <h2>Existing Packages</h2>
        {loading ? (
          <div className="empty-state">Loading…</div>
        ) : sortedPackages.length === 0 ? (
          <div className="empty-state">
            No packages defined yet. Use the form to create one.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Credits</th>
                <th>Price (USD)</th>
                <th>PIX</th>
                <th>Status</th>
                <th>Stripe Price ID</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedPackages.map((pkg) => (
                <tr key={pkg.id}>
                  <td>
                    <strong>{pkg.name}</strong>
                    {pkg.description && (
                      <div className="text-muted">{pkg.description}</div>
                    )}
                  </td>
                  <td>{pkg.credits.toLocaleString()}</td>
                  <td>${pkg.priceUsd.toFixed(2)}</td>
                  <td>
                    {pkg.pixShow && pkg.pixPrice ? (
                      <span>
                        {pkg.pixPrice.toLocaleString()} PIX
                        {pkg.pixDiscountPercent ? ` (${pkg.pixDiscountPercent}% off)` : ''}
                      </span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    <span className={pkg.active ? 'badge badge-active' : 'badge badge-muted'}>
                      {pkg.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="monospace">{pkg.stripePriceId || '—'}</td>
                  <td className="actions">
                    <button className="btn-link" onClick={() => handleEdit(pkg)}>
                      Edit
                    </button>
                    <button className="btn-link danger" onClick={() => handleDelete(pkg)}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h2>{editingId ? 'Edit Package' : 'Create Package'}</h2>
              <button type="button" className="modal-close" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>
            {error && <div className="alert alert-error">{error}</div>}
            <section className="package-form">
              <form onSubmit={handleSubmit}>
                <label>
                  Name
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </label>
                <label>
                  Description
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </label>
                <div className="form-row">
                  <label>
                    Credits
                    <input
                      type="number"
                      min={0}
                      value={formData.credits}
                      onChange={(e) => setFormData({ ...formData, credits: Number(e.target.value) })}
                      required
                    />
                  </label>
                  <label>
                    Price (USD)
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={formData.priceUsd}
                      onChange={(e) => setFormData({ ...formData, priceUsd: Number(e.target.value) })}
                      required
                    />
                  </label>
                </div>
                <label>
                  Stripe Price ID
                  <input
                    type="text"
                    value={formData.stripePriceId}
                    onChange={(e) => setFormData({ ...formData, stripePriceId: e.target.value })}
                    placeholder="price_..."
                  />
                </label>
                <div className="pix-section">
                  <div className="pix-section-title">Pay with PIX</div>
                  <div className="form-row">
                    <label>
                      PIX Price
                      <input
                        type="number"
                        min={0}
                        value={formData.pixPrice}
                        onChange={(e) => setFormData({ ...formData, pixPrice: Number(e.target.value) })}
                      />
                    </label>
                    <label>
                      Discount % (display only)
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={formData.pixDiscountPercent}
                        onChange={(e) => setFormData({ ...formData, pixDiscountPercent: Number(e.target.value) })}
                      />
                    </label>
                  </div>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={formData.pixShow}
                      onChange={(e) => setFormData({ ...formData, pixShow: e.target.checked })}
                    />
                    Show PIX option on client billing page
                  </label>
                </div>
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  Active
                </label>
                <div className="form-actions">
                  <button className="btn-primary" type="submit" disabled={saving}>
                    {saving ? 'Saving…' : editingId ? 'Update Package' : 'Create Package'}
                  </button>
                  <button type="button" className="btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                </div>
              </form>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}
